import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout, Input, Button, Typography, message as antMessage } from 'antd';
import {
  SendOutlined,
  FilterOutlined,
  LogoutOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { agentAPI, estimationAPI } from '../../services/api';
import useAgentStream from './useAgentStream';
import MessageList from './MessageList';
import SuggestionChips from './SuggestionChips';
import ChatSidebar from './ChatSidebar';
import ProjectUpload from '../Ingest/ProjectUpload';
import './AgentChat.css';

const { Header, Content, Sider } = Layout;
const { TextArea } = Input;
const { Title, Text } = Typography;

const AgentChat = () => {
  const { user, logout, hasPermission, isAdmin } = useAuth();
  const navigate = useNavigate();
  const canEstimate = hasPermission('kostenschaetzung');
  const canUpload = hasPermission('datenimport');

  const [uploadOpen, setUploadOpen] = useState(false);
  const [chats, setChats] = useState([]);
  const [estimations, setEstimations] = useState([]);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const refreshChats = useCallback(async () => {
    try {
      const response = await agentAPI.listConversations();
      setChats(response.data.conversations || []);
    } catch (e) {
      console.debug('Could not load chat history');
    }
  }, []);

  const refreshEstimations = useCallback(async () => {
    if (!canEstimate) return;
    try {
      const response = await estimationAPI.list();
      setEstimations(response.data.estimations || []);
    } catch (e) {
      console.debug('Could not load estimations');
    }
  }, [canEstimate]);

  const {
    messages,
    conversationId,
    isStreaming,
    sendMessage,
    startNewChat,
    loadConversation,
  } = useAgentStream({ onTurnComplete: refreshChats });

  useEffect(() => {
    refreshChats();
    refreshEstimations();
  }, [refreshChats, refreshEstimations]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  const handleSend = (text) => {
    const value = (text ?? input).trim();
    if (!value || isStreaming) return;
    setInput('');
    sendMessage(value);
  };

  const handleSelectChat = async (chatId) => {
    try {
      await loadConversation(chatId);
    } catch (e) {
      antMessage.error('Chat konnte nicht geladen werden');
      refreshChats();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Layout className="agent-chat-layout">
      <Sider width={280} theme="light" className="agent-chat-sider">
        <ChatSidebar
          chats={chats}
          activeConversationId={conversationId}
          onNewChat={startNewChat}
          onSelectChat={handleSelectChat}
          onChatsChanged={refreshChats}
          estimations={estimations}
          isStreaming={isStreaming}
          onUpload={canUpload ? () => setUploadOpen(true) : undefined}
        />
      </Sider>

      <Layout>
        <Header className="agent-chat-header">
          <div className="agent-chat-header-left">
            <img src="/logo_black.png" alt="PERITA" className="agent-chat-logo" />
            <span className="wz-header-divider" />
            <span className="wz-wordmark wz-header-wordmark">Wizzy</span>
          </div>
          <div className="agent-chat-header-right">
            {isAdmin && (
              <Button icon={<TeamOutlined />} onClick={() => navigate('/admin')}>
                Benutzerverwaltung
              </Button>
            )}
            <Button icon={<FilterOutlined />} onClick={() => navigate('/search')}>
              Klassische Suche
            </Button>
            <span className="agent-chat-user">{user?.username}</span>
            <Button type="text" icon={<LogoutOutlined />} onClick={handleLogout}>
              Abmelden
            </Button>
          </div>
        </Header>

        <Content className="agent-chat-content">
          <div className="agent-chat-messages">
            {messages.length === 0 ? (
              <div className="agent-chat-empty">
                <Title level={3}>Wie kann ich helfen?</Title>
                <Text type="secondary">
                  Fragen Sie nach Referenzprojekten, Kostenkennwerten, Vergleichen oder
                  starten Sie eine Kostenschätzung — auf Deutsch oder Englisch.
                </Text>
                <SuggestionChips onSelect={handleSend} disabled={isStreaming} />
              </div>
            ) : (
              <div className="agent-chat-message-list">
                <MessageList messages={messages} isStreaming={isStreaming} />
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          <div className="agent-chat-input-bar">
            <div className="agent-chat-input-inner">
              <TextArea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Frage zu Bauprojekten und Kosten stellen… (Shift+Enter für neue Zeile)"
                autoSize={{ minRows: 1, maxRows: 6 }}
                disabled={isStreaming}
                variant="borderless"
              />
              <Button
                type="primary"
                shape="circle"
                icon={<SendOutlined />}
                onClick={() => handleSend()}
                loading={isStreaming}
                disabled={!input.trim() && !isStreaming}
              />
            </div>
          </div>
        </Content>
      </Layout>
      {canUpload && (
        <ProjectUpload open={uploadOpen} onClose={() => setUploadOpen(false)} />
      )}
    </Layout>
  );
};

export default AgentChat;
